import { useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface Report {
  id: string;
  projectId: string;
  type: string;
  format: string;
  name: string;
  status: string;
  path: string | null;
  bytes: number;
  directDownloadPath: string | null;
}

export interface ReportPage {
  pageNumber: number;
  totalPages: number;
  numberReports: number;
  totalReports: number;
  reports: Report[];
}

const LIMIT = 20;

export function useReports(projectId: string, page: number) {
  return useQuery<ReportPage>({
    queryKey: ['reports', projectId, page],
    queryFn: () => api.get<ReportPage>(`/project/${projectId}/report?page=${page}&limit=${LIMIT}`),
    enabled: !!projectId,
    // Keep the current page's rows on screen while the next page loads.
    placeholderData: keepPreviousData,
  });
}

/**
 * FeedService.ReportUpdate / ReportComplete. Both frames carry the same fields as a Report
 * row, so the cached row can be replaced outright: Update arrives with
 * `status: "Generating"`, `bytes: 0` and null paths, Complete with `status: "Available"`
 * and the real byte count and download URL.
 */
export type ReportEventMessage = Report;

/**
 * Every report websocket event: row status and the completion toast. Mounted once by
 * WebSocketBridge — a report outlives the Reports tab, and the toast has always been
 * app-wide ("you could be on any page when the report finishes").
 *
 * The toast moved here from Angular's ReportService, keeping its audit-tracked download
 * button: notify.ts routes through Angular's NotificationService, whose template $compiles
 * the button HTML (see directives/utilities/bindHtml.js), so iio-audit still runs.
 */
export function useReportEvents() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const patch = (message: ReportEventMessage) => {
      if (!message?.id || !message.projectId) return;

      let matched = false;
      // The page number is part of the query key, so patch whichever page holds the row.
      queryClient.setQueriesData<ReportPage>({ queryKey: ['reports', message.projectId] }, prev => {
        if (!prev?.reports?.some(r => r.id === message.id)) return prev;
        matched = true;
        return { ...prev, reports: prev.reports.map(r => (r.id === message.id ? { ...r, ...message } : r)) };
      });

      // A report started elsewhere isn't in any cached page yet.
      if (!matched) {
        queryClient.invalidateQueries({ queryKey: ['reports', message.projectId] });
      }
    };

    const offUpdate = onAngularEvent('FeedService.ReportUpdate', patch);
    const offComplete = onAngularEvent('FeedService.ReportComplete', (message: ReportEventMessage) => {
      const auditPath = (message.directDownloadPath ?? '').split('?')[0];
      notifySuccess({
        title: 'Report available',
        description: `Report: ${message.name} has completed.`,
        buttons: `<a class="btn btn-primary" iio-audit="{projectId: '${message.projectId}', description: 'Download Report', actionDetails: {reportName: '${message.name}', uri: '${auditPath}'}}" href="${message.directDownloadPath}"><i class="icon-download-alt"></i> Download</a>`,
      });
      patch(message);
    });

    return () => { offUpdate(); offComplete(); };
  }, [queryClient]);
}

export interface ReportField {
  type: string;
  name: string;
  outputName: string;
  isEnabled: boolean;
}

export interface CreateReportInput {
  projectId: string;
  // The `_search` variants are used when the report is launched from the search-results
  // grid (SearchController:142, :172).
  type:
    | 'pre_processing_report'
    | 'post_processing_report'
    | 'resource_report'
    | 'tag_report'
    | 'resource_report_search'
    | 'tag_search_report';
  format: string;
  name: string;
  multiValueCharCode: number;
  dateTimeFormat: string;
  hasHeaderRow: boolean;
  fields: ReportField[];
  collectionIds: string[];
  // Column filters come from the grid filter model (M3e); null until then. Pre/post
  // processing reports still pass the structured object.
  columnFilters?: { operator: number; fields: unknown[]; searchExpressions: unknown[] } | null;
  // Resource/Tag reports over a grid view also carry the tag filter and (tag report)
  // the tag-category flags; search fields are set only when launched from a search.
  filter?: unknown;
  includeProjectTags?: boolean;
  includeSystemTags?: boolean;
  includeDomainTags?: boolean;
  includeBatchTags?: boolean;
  isRawSearch?: boolean;
  query?: unknown;
  searchExpression?: unknown;
}

export function useCreateReport(projectId: string) {
  return useToastMutation<unknown, CreateReportInput>(
    ['reports', projectId],
    (input) => api.post(`/project/${projectId}/report`, input),
    {
      success: (_d, v) => ({ title: 'Report started', description: `Name: ${v.name}`, timeout: 5000 }),
    },
  );
}
